/**
 * MachineIllustration — the shaded vector machine drawings.
 *
 * A literal port of the demo's "Machine illustrations — shaded vector,
 * draggable callouts" script block, `OEM+_Platform_Demo.html` **lines
 * 1501–1629**:
 *
 * | Demo lines | What |
 * |---|---|
 * | 1503–1524 | primitives — `G` `K` `S` `R` `Pg` `Pa` `C` `L` `hiLine` `tyre` `louvre` `panelLines` `bolt` `light` `beacon` `hose` `cylinder` `shadow` `capsule` `gloss` |
 * | 1527–1537 | `sideLHD` / `planLHD` |
 * | 1539–1567 | `sideTruck` / `planTruck` |
 * | 1570–1586 | `sideJumbo` / `planJumbo` |
 * | 1589–1605 | `sideLonghole` / `planLonghole` |
 * | 1607–1613 | `ANCH`, `SIDES`, `PLANS` |
 * | 1615–1622 | `illustration(f, health, sel, view)` |
 * | 1627–1629 | `enableLabelDrag` — **not ported** |
 *
 * Every coordinate, gradient stop, loop count and magic number inside the
 * eight body builders is the demo's, transcribed unchanged. This is a fidelity
 * port: shapes are not simplified, details are not dropped, and the livery is
 * not re-themed.
 *
 * ## The drawing is a typed node tree, not markup
 *
 * The demo builds each machine by concatenating SVG **strings** (`o += R(…)`).
 * This port keeps the demo's helper names, arguments and arithmetic but has
 * every helper return {@link SvgNode} values instead, which the builders
 * accumulate into an array and {@link SvgNodes} renders recursively with
 * `createElement`. Nothing in this file produces or injects HTML: the repo gate
 * `npm run check:oem-handlers` forbids `dangerouslySetInnerHTML`, and ported
 * demo markup has to be modelled as typed blocks and rendered as JSX. The
 * change is one of representation only — no coordinate, gradient stop, loop
 * count or magic number moved.
 *
 * Attribute names stay exactly as the demo writes them: the helpers build keys
 * with `_` and {@link S} rewrites those to `-`, so the DOM sees the demo's
 * `stroke-width`, `stroke-linejoin`, `text-anchor`, `font-family`. React passes
 * hyphenated SVG attributes straight through, so no element mixes the
 * hyphenated and camelCase spellings.
 *
 * ## Deviations from the demo
 *
 * 1. **Deterministic gradient uid.** The demo namespaces its gradient ids with
 *    a module counter (`UID = 'il' + (++ILN)`, demo line 1615), so the same
 *    machine renders differently on every call. This port derives the uid from
 *    `machineId` + `view` instead. Nothing here calls a counter,
 *    `Math.random()` or `Date.now()`, so a given machine's SVG is identical
 *    across renders and the parity snapshots stay stable. React keys are
 *    likewise deterministic — a node's tag plus its index within its parent.
 * 2. **No callout drag.** `enableLabelDrag` (demo lines 1627–1629) and its
 *    session-remembered `LABELPOS` store (demo line 1614) are omitted. Every
 *    callout renders at the demo's computed default position. The drag grip
 *    glyph (`⋮⋮`) the demo draws at the right edge of each label is dropped with
 *    it — it advertised an affordance that no longer exists. The label width
 *    `lw` is unchanged, so the leader-line geometry still matches the demo.
 * 3. **Band colours are tokens.** The demo hard-codes
 *    `{major:'#2B1B2E', vpoor:'#D32F2F', poor:'#E67E22'}` for the callouts
 *    (demo line 1618); this port uses `var(--band-major)` / `var(--band-vpoor)`
 *    / `var(--band-poor)` from `src/versions/v1/styles/oem.css` so the callouts
 *    follow the theme. The **livery** hexes inside the body builders (the
 *    pinks, steels, glass, tyre blacks) are studio-render paint, not theme
 *    tokens, and stay verbatim as hex — except that the body paint follows
 *    the brand: {@link LIVERY} holds the demo's pinks verbatim for Meridian
 *    and a yellow of the same shape for Komatsu (see deviation 7).
 * 4. **`h.band` is derived, not read.** Demo line 1866 mutates every health row
 *    in place to add `band`; this port calls `healthBand(h)` from `oem-engine`.
 *    The demo's `class="hs ${h.status}"` (its post-mutation
 *    `critical|watch|ok`) comes from `healthAttention(h)` for the same reason.
 * 5. **`sdrill` has no drawing.** The demo's `SIDES`/`PLANS` (demo line 1613)
 *    cover only `lhd`, `truck`, `jumbo` and `longhole`. No surface-drill body
 *    builder is invented: `hasIllustration('sdrill')` is `false` and the
 *    component returns `null`.
 * 6. **`hiLine` not ported.** Demo line 1512 defines it, but no body builder
 *    calls it; an unused module-level const fails `noUnusedLocals` in
 *    `tsconfig.json`. Its one line is `L(x1, y1, x2, y2, { c: K.hi, w: 2 })`.
 * 7. **Paint follows the brand.** The demo hard-codes Meridian pink in the
 *    `gp`/`gpd` gradients (demo line 1616), `K.pinkFlat` (demo line 1505) and
 *    the selected-pin ink. Here those come from {@link LIVERY}, keyed on the
 *    active brand — byte-identical to the demo under Meridian, Komatsu yellow
 *    (`#FFCC00`, the brand's `--brand-secondary`) under Komatsu.
 *
 * @see docs/foundations/4-layer-architecture.md
 */

import { createElement, type KeyboardEvent, type ReactNode } from 'react';

import type { ComponentHealth } from '../../data/core';
import type { MachineClass, ModelCode } from '../../schema/machine';
import { healthAttention, healthBand } from '../oem-engine';
import { useBrand, type BrandId } from '../BrandContext';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Which of the two drawings to render. The demo's `view` argument, line 1615. */
export type IllustrationView = 'side' | 'plan';

/** Props for {@link MachineIllustration}. */
export interface MachineIllustrationProps {
  /** Machine id — the SVG's unique gradient-id namespace, and part of its aria. */
  machineId: string;
  /** Model code; selects the per-model variants inside the body builders. */
  model: ModelCode;
  /** Machine class; selects the body builder. */
  cls: MachineClass;
  /** Side or plan. */
  view: IllustrationView;
  /** This machine's rows from the condition register. */
  health: readonly ComponentHealth[];
  /** Currently selected component name, or null. */
  selected?: string | null;
  /** Called with the component name when a hotspot, callout or pin is activated. */
  onSelect?: (component: string) => void;
}

// ---------------------------------------------------------------------------
// The node tree
// ---------------------------------------------------------------------------

/**
 * One SVG element in a ported drawing.
 *
 * This is what the demo's string concatenation becomes. Attribute keys are
 * already in their final, DOM spelling (hyphenated where the demo hyphenates);
 * {@link S} does the `_` → `-` rewrite when it builds the node.
 */
interface SvgNode {
  /** Element name — `'rect' | 'path' | 'circle' | 'g' | 'text' | …`. */
  readonly tag: string;
  /** Attributes, already named exactly as they reach the DOM. */
  readonly attrs: Readonly<Record<string, string | number>>;
  /** Child elements, for container tags such as `<g>` and `<linearGradient>`. */
  readonly children?: readonly SvgNode[];
  /** Text content, for `<text>` nodes. */
  readonly text?: string;
}

/**
 * Hyphenated SVG attribute → the camelCase prop React expects.
 *
 * The demo writes `stroke-width`, `stop-color` and friends, and this port keeps
 * the demo's spelling in the node tree so a ported builder reads like its
 * source. React, however, owns a canonical camelCase name for every standard
 * SVG attribute and warns on the hyphenated form ("Invalid DOM property
 * `stroke-width`. Did you mean `strokeWidth`?"), so the rewrite happens here,
 * at the single boundary where a node becomes an element.
 *
 * `data-` and `aria-` attributes are deliberately left alone: React passes
 * those through verbatim and camel-casing them would break them.
 */
function reactAttr(name: string): string {
  if (name.startsWith('data-') || name.startsWith('aria-')) return name;
  return name.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
}

/**
 * Render one node and its subtree.
 *
 * The key is the node's tag plus its index within its parent, so it is stable
 * and unique without a counter or a random value.
 */
function renderNode(node: SvgNode, index: number): ReactNode {
  const children: ReactNode = node.children
    ? node.children.map((child, i) => renderNode(child, i))
    : node.text;
  const props: Record<string, string | number> = { key: `${node.tag}-${index}` };
  for (const [name, value] of Object.entries(node.attrs)) props[reactAttr(name)] = value;
  return createElement(node.tag, props, children);
}

/** Render a list of nodes — the bridge from the ported builders to React. */
function SvgNodes({ nodes }: { nodes: readonly SvgNode[] }): JSX.Element {
  return <>{nodes.map((node, i) => renderNode(node, i))}</>;
}

/**
 * Append one node, or a list of them, to a builder's accumulator.
 *
 * Stands in for the demo's `o += …`. Helpers that emit a single element return
 * an {@link SvgNode}; helpers that emit several (`tyre`, `louvre`, `light`,
 * `beacon`, `hose`, `panelLines`) return an array, and both append the same way.
 */
function add(o: SvgNode[], n: SvgNode | readonly SvgNode[]): void {
  if (Array.isArray(n)) o.push(...(n as readonly SvgNode[]));
  else o.push(n as SvgNode);
}

// ---------------------------------------------------------------------------
// Primitive helpers — demo lines 1503–1524
// ---------------------------------------------------------------------------

/** Anything that can be an SVG attribute value in the demo's `S()`. */
type AttrValue = string | number;

/** An attribute bag. Keys use `_` where the SVG attribute uses `-`. */
type Attrs = Readonly<Record<string, AttrValue>>;

/** Options accepted by the stroked-shape primitives. */
interface ShapeOpts {
  /** Stroke colour; defaults to `K.line`. */
  s?: string;
  /** Stroke width; defaults to 1. */
  sw?: number;
  /** Corner radius (rects only); defaults to 3. */
  rx?: number;
  /** Extra raw attributes. */
  a?: Attrs;
}

/** Options accepted by {@link L}. */
interface LineOpts {
  /** Stroke colour; defaults to `K.line`. */
  c?: string;
  /** Stroke width; defaults to 1. */
  w?: number;
  /** Extra raw attributes. */
  a?: Attrs;
}

/** A `[x1, y1, x2, y2]` segment for {@link panelLines}. */
type Segment = readonly [number, number, number, number];

/**
 * Demo line 1504. `G` — the eight gradient references, namespaced by `UID`.
 *
 * The demo mutates a module-level `UID` immediately before calling a body
 * builder; this port builds one `Gradients` per render from the deterministic
 * uid and hands it to the builder, so nothing is global and nothing is mutated.
 */
interface Gradients {
  pink(): string;
  pinkDark(): string;
  white(): string;
  steel(): string;
  dark(): string;
  glass(): string;
  tyre(): string;
  rim(): string;
}

/** The four primitives that close over `UID` in the demo, bound to one uid. */
interface Prims {
  /** Demo line 1504. */
  G: Gradients;
  /** Demo line 1522. */
  shadow(cx: number, cy: number, rx: number): SvgNode;
  /** Demo lines 1513–1514. */
  tyre(cx: number, cy: number, r: number): SvgNode[];
  /** Demo line 1521. */
  cylinder(x1: number, y1: number, x2: number, y2: number, r: number): SvgNode;
}

/** One body builder. Demo lines 1527–1605. */
type Builder = (model: ModelCode, p: Prims) => SvgNode[];

/**
 * Demo line 1505. `K` — the flat ink colours shared by the primitives. The
 * demo's `pinkFlat` is paint, so it lives in {@link LIVERY} as `flat`.
 */
const K = {
  line: '#2A2530',
  line2: '#4a4350',
  hi: 'rgba(255,255,255,.55)',
  warn: '#F2A20C',
  crit: '#E53935',
} as const;

/** The body paint of one brand: the two gradients, the flat ink and the pin ink. */
export interface LiveryPalette {
  /** `gp` — the main paint gradient, top → bottom. */
  readonly paint: readonly [string, string, string];
  /** `gpd` — the shadowed paint gradient (chassis rails, lower panels). */
  readonly paintDark: readonly [string, string];
  /** Demo `K.pinkFlat` — the flat paint colour. */
  readonly flat: string;
  /** The selected anchor pin's fill and every pin's stroke. */
  readonly pin: string;
}

/**
 * Body paint per brand. Meridian is the demo's pink verbatim (demo lines 1505
 * and 1616); Komatsu is the same three-stop shape around Komatsu yellow
 * `#FFCC00` (hsl 48° 100% 50%) — a lighter top stop, a deeper amber base —
 * so the shading reads the same under both.
 */
export const LIVERY: Readonly<Record<BrandId, LiveryPalette>> = {
  meridian: {
    paint: ['#FF6FB4', '#EA3C93', '#B8226F'],
    paintDark: ['#C92B7A', '#8E1A56'],
    flat: '#E8368F',
    pin: '#B8226F',
  },
  komatsu: {
    paint: ['#FFE566', '#FFCC00', '#CC9900'],
    paintDark: ['#E0A800', '#996B00'],
    flat: '#FFCC00',
    pin: '#8F6400',
  },
};

/**
 * Demo line 1506. `S` — the element factory every other primitive funnels
 * through. Attribute keys have `_` rewritten to `-`, so `stroke_width` becomes
 * `stroke-width`, exactly as the demo's serialiser did.
 */
function S(tag: string, a: Attrs, inner = ''): SvgNode {
  const attrs: Record<string, AttrValue> = {};
  for (const [k, v] of Object.entries(a)) attrs[k.replace(/_/g, '-')] = v;
  return inner ? { tag, attrs, text: inner } : { tag, attrs };
}

/** Demo line 1507. `R` — a rounded rect. */
const R = (x: number, y: number, w: number, h: number, f: string, o: ShapeOpts = {}): SvgNode =>
  S('rect', {
    x,
    y,
    width: w,
    height: h,
    fill: f,
    stroke: o.s ?? K.line,
    stroke_width: o.sw ?? 1,
    rx: o.rx ?? 3,
    ...(o.a || {}),
  });

/** Demo line 1508. `Pg` — a polygon. */
const Pg = (pts: string, f: string, o: ShapeOpts = {}): SvgNode =>
  S('polygon', {
    points: pts,
    fill: f,
    stroke: o.s ?? K.line,
    stroke_width: o.sw ?? 1,
    stroke_linejoin: 'round',
    ...(o.a || {}),
  });

/** Demo line 1509. `Pa` — a path. */
const Pa = (d: string, f: string, o: ShapeOpts = {}): SvgNode =>
  S('path', {
    d,
    fill: f,
    stroke: o.s ?? K.line,
    stroke_width: o.sw ?? 1,
    stroke_linejoin: 'round',
    ...(o.a || {}),
  });

/** Demo line 1510. `C` — a circle. */
const C = (cx: number, cy: number, r: number, f: string, o: ShapeOpts = {}): SvgNode =>
  S('circle', { cx, cy, r, fill: f, stroke: o.s ?? K.line, stroke_width: o.sw ?? 1 });

/** Demo line 1511. `L` — a round-capped line. */
const L = (x1: number, y1: number, x2: number, y2: number, o: LineOpts = {}): SvgNode =>
  S('line', {
    x1,
    y1,
    x2,
    y2,
    stroke: o.c || K.line,
    stroke_width: o.w || 1,
    stroke_linecap: 'round',
    ...(o.a || {}),
  });

/**
 * Demo line 1512. `hiLine` — **not ported**. Dead code in the demo: no body
 * builder calls it, and an unused module-level const fails `noUnusedLocals`.
 * For the record, its whole body is `L(x1, y1, x2, y2, { c: K.hi, w: 2 })`.
 */

/** Demo line 1515. `louvre` — `n` evenly spaced vent slats inside a box. */
function louvre(x: number, y: number, w: number, h: number, n: number): SvgNode[] {
  const s: SvgNode[] = [];
  for (let i = 0; i < n; i++) {
    const yy = y + ((i + 0.5) * h) / n;
    s.push(L(x + 4, yy, x + w - 4, yy, { c: '#8d8794', w: 1.4 }));
    s.push(L(x + 4, yy + 1.6, x + w - 4, yy + 1.6, { c: 'rgba(255,255,255,.5)', w: 0.8 }));
  }
  return s;
}

/** Demo line 1516. `panelLines` — the dark seams between body panels. */
const panelLines = (pts: readonly Segment[]): SvgNode[] =>
  pts.map((p) => L(p[0], p[1], p[2], p[3], { c: 'rgba(0,0,0,.28)', w: 1 }));

/** Demo line 1517. `bolt` — one panel fastener. */
const bolt = (x: number, y: number): SvgNode => C(x, y, 2.2, '#6b6570', { s: '#2a2530', sw: 0.6 });

/** Demo line 1518. `light` — a warm work lamp with a white hot spot. */
const light = (x: number, y: number, r = 5): SvgNode[] => [
  C(x, y, r, '#FFF6C7', { s: '#8a7d3a' }),
  C(x, y, r * 0.45, '#fff', { s: 'none' }),
];

/** Demo line 1519. `beacon` — the amber roof beacon on its stub mount. */
const beacon = (x: number, y: number): SvgNode[] => [
  R(x - 5, y - 10, 10, 10, '#FF9E3D', { rx: 3, s: '#8a4a12' }),
  R(x - 3, y - 13, 6, 3, '#2A2530', { rx: 1 }),
];

/** Demo line 1520. `hose` — a sagging hydraulic hose, drawn twice for the sheen. */
const hose = (x1: number, y1: number, x2: number, y2: number, sag: number, w = 2.6): SvgNode[] => [
  S('path', {
    d: `M${x1},${y1} Q${(x1 + x2) / 2},${Math.max(y1, y2) + sag} ${x2},${y2}`,
    fill: 'none',
    stroke: '#1b191e',
    stroke_width: w,
    stroke_linecap: 'round',
  }),
  S('path', {
    d: `M${x1},${y1} Q${(x1 + x2) / 2},${Math.max(y1, y2) + sag} ${x2},${y2}`,
    fill: 'none',
    stroke: 'rgba(255,255,255,.25)',
    stroke_width: 0.8,
  }),
];

/** Demo line 1523. `capsule` — a fully rounded rect (extinguishers, exhaust caps). */
const capsule = (x: number, y: number, w: number, h: number, f: string, o: ShapeOpts = {}): SvgNode =>
  R(x, y, w, h, f, { rx: h / 2, ...o });

/** Demo line 1524. `gloss` — a translucent white highlight bar. */
const gloss = (x: number, y: number, w: number, h: number, rx = 6): SvgNode =>
  R(x, y, w, h, 'rgba(255,255,255,.22)', { rx, s: 'none' });

/**
 * Demo lines 1504 / 1513–1514 / 1521 / 1522. The four primitives that reference
 * the gradient namespace, bound to one deterministic `uid`.
 */
function makePrims(uid: string): Prims {
  /** Demo line 1504. `G`. */
  const G: Gradients = {
    pink: () => `url(#gp${uid})`,
    pinkDark: () => `url(#gpd${uid})`,
    white: () => `url(#gw${uid})`,
    steel: () => `url(#gs${uid})`,
    dark: () => `url(#gk${uid})`,
    glass: () => `url(#gg${uid})`,
    tyre: () => `url(#gt${uid})`,
    rim: () => `url(#gr${uid})`,
  };

  /** Demo lines 1513–1514. `tyre` — 18 tread blocks, rim, 8 wheel nuts, hub. */
  function tyre(cx: number, cy: number, r: number): SvgNode[] {
    const s: SvgNode[] = [C(cx, cy, r, G.tyre(), { sw: 1.2 })];
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * Math.PI * 2;
      const a2 = a + 0.09;
      s.push(
        Pg(
          `${cx + Math.cos(a) * r * 0.72},${cy + Math.sin(a) * r * 0.72} ${cx + Math.cos(a) * r},${cy + Math.sin(a) * r} ${cx + Math.cos(a2) * r},${cy + Math.sin(a2) * r} ${cx + Math.cos(a2) * r * 0.72},${cy + Math.sin(a2) * r * 0.72}`,
          '#151318',
          { s: 'none' },
        ),
      );
    }
    s.push(C(cx, cy, r * 0.7, '#26232a', { s: '#111' }));
    s.push(C(cx, cy, r * 0.5, G.rim(), { sw: 1 }));
    s.push(C(cx, cy, r * 0.34, '#5d5764', { s: '#3a353f' }));
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      s.push(C(cx + Math.cos(a) * r * 0.42, cy + Math.sin(a) * r * 0.42, r * 0.035, '#2b2730', { s: 'none' }));
    }
    s.push(C(cx, cy, r * 0.14, '#3a353f', { s: '#222' }));
    return s;
  }

  /** Demo line 1521. `cylinder` — a hydraulic ram: barrel, rod, sheen, pin eye. */
  const cylinder = (x1: number, y1: number, x2: number, y2: number, r: number): SvgNode => {
    const a = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
    const len = Math.hypot(x2 - x1, y2 - y1);
    return {
      tag: 'g',
      attrs: { transform: `translate(${x1},${y1}) rotate(${a})` },
      children: [
        R(0, -r, len * 0.58, 2 * r, G.steel(), { rx: r }),
        R(len * 0.55, -r * 0.5, len * 0.45, r, '#c9c5ce', { rx: r * 0.5, s: '#6a646f' }),
        R(len * 0.55, -r * 0.5, len * 0.45, r * 0.35, 'rgba(255,255,255,.6)', { rx: r * 0.3, s: 'none' }),
        C(0, 0, r * 0.9, '#5d5764', { s: '#2a2530' }),
      ],
    };
  };

  /** Demo line 1522. `shadow` — the soft contact ellipse under the machine. */
  const shadow = (cx: number, cy: number, rx: number): SvgNode =>
    S('ellipse', { cx, cy, rx, ry: 10, fill: `url(#gsh${uid})` });

  return { G, shadow, tyre, cylinder };
}

// ---------------------------------------------------------------------------
// Body builders — LOADER (demo lines 1526–1537)
// ---------------------------------------------------------------------------

/** Demo lines 1527–1536. `sideLHD` — the loader, side elevation. */
function sideLHD(model: ModelCode, p: Prims): SvgNode[] {
  const { G, shadow, tyre, cylinder } = p;
  const bat = model === 'ML-21E';
  // Demo line 1527 also declares `const small = model === 'ML-14'`, but nothing
  // in the builder reads it. Dropped: an unused local fails `noUnusedLocals`.
  const remote = model === 'ML-21R';
  const gy = 300;
  const o: SvgNode[] = [shadow(470, gy + 6, 440)];
  // rear frame
  add(o, Pa(`M560,${gy - 120} h290 a12,12 0 0 1 12,12 v84 a10,10 0 0 1 -10,10 h-292 z`, G.pink()));
  add(o, gloss(572, gy - 112, 270, 10));
  if (bat) {
    add(o, R(598, gy - 108, 236, 62, G.white(), { rx: 6 }));
    for (let i = 0; i < 6; i++) {
      add(o, R(606 + i * 38, gy - 102, 30, 50, '#d6d1da', { rx: 3, s: '#7d7784' }));
      add(o, R(608 + i * 38, gy - 100, 26, 8, 'rgba(255,255,255,.7)', { rx: 2, s: 'none' }));
      add(o, C(621 + i * 38, gy - 62, 2.5, '#39b06a', { s: 'none' }));
    }
    add(
      o,
      S(
        'text',
        {
          x: 716,
          y: gy - 113,
          text_anchor: 'middle',
          font_size: 8.5,
          fill: '#3a353f',
          font_family: 'IBM Plex Mono,monospace',
          font_weight: 600,
        },
        'LFP 300 kWh · 640 V',
      ),
    );
    add(o, R(836, gy - 100, 20, 28, '#2a2530', { rx: 3 }));
    add(o, C(846, gy - 86, 5, '#39b06a', { s: 'none' }));
  } else {
    add(o, R(600, gy - 110, 150, 62, G.white(), { rx: 6 }));
    add(o, louvre(606, gy - 104, 138, 50, 7));
    add(o, R(768, gy - 146, 16, 36, G.dark(), { rx: 3 }));
    add(o, capsule(762, gy - 158, 28, 14, '#7d7784'));
    add(o, R(760, gy - 112, 80, 52, '#3a353f', { rx: 4 }));
    add(o, louvre(764, gy - 108, 72, 44, 6));
  }
  add(o, R(560, gy - 48, 304, 22, G.pinkDark(), { rx: 4 }));
  add(
    o,
    panelLines([
      [600, gy - 120, 600, gy - 48],
      [750, gy - 120, 750, gy - 48],
    ]),
  );
  add(o, [bolt(575, gy - 40), bolt(700, gy - 40), bolt(840, gy - 40)]);
  // mid: hydraulic tank/pump module
  add(o, Pa(`M470,${gy - 104} h92 v66 h-92 a8,8 0 0 1 -8,-8 v-50 a8,8 0 0 1 8,-8 z`, G.pink()));
  add(o, R(482, gy - 94, 66, 24, G.white(), { rx: 3 }));
  add(o, R(486, gy - 90, 58, 6, 'rgba(255,255,255,.7)', { rx: 2, s: 'none' }));
  add(o, R(484, gy - 64, 40, 16, '#5d5764', { rx: 3 }));
  add(o, hose(500, gy - 64, 470, gy - 30, 10));
  // cab / canopy (side-mounted)
  if (remote) {
    add(o, R(598, gy - 192, 76, 78, G.white(), { rx: 8 }));
    add(o, R(606, gy - 184, 60, 32, '#c5c0ca', { rx: 3 }));
    add(o, R(608, gy - 146, 56, 26, '#3a353f', { rx: 3 }));
    add(o, L(636, gy - 192, 636, gy - 240, { w: 2.4 }));
    add(o, C(636, gy - 244, 6, '#2a2530'));
    add(o, R(586, gy - 216, 20, 20, '#2a2530', { rx: 4 }));
    add(o, C(596, gy - 206, 5, '#3aa0ff', { s: 'none' }));
    add(
      o,
      S(
        'text',
        {
          x: 636,
          y: gy - 124,
          text_anchor: 'middle',
          font_size: 8,
          fill: '#3a353f',
          font_family: 'IBM Plex Mono,monospace',
        },
        'TELE-REMOTE',
      ),
    );
  } else {
    add(o, Pa(`M588,${gy - 208} h104 a8,8 0 0 1 8,8 v82 h-120 v-82 a8,8 0 0 1 8,-8 z`, G.white()));
    add(o, R(600, gy - 198, 82, 54, G.glass(), { rx: 4, s: '#5b6b74' }));
    add(o, L(606, gy - 150, 640, gy - 194, { c: 'rgba(255,255,255,.75)', w: 3 }));
    add(o, R(582, gy - 214, 116, 9, G.dark(), { rx: 3 }));
    add(o, R(604, gy - 138, 74, 14, '#3a353f', { rx: 3 }));
    add(o, beacon(690, gy - 216));
  }
  // articulation
  add(o, R(438, gy - 106, 34, 68, G.dark(), { rx: 6 }));
  add(o, C(455, gy - 72, 9, '#8d8794', { s: '#2a2530' }));
  add(o, C(455, gy - 72, 4, '#3a353f', { s: 'none' }));
  // front frame
  add(o, Pa(`M300,${gy - 98} h150 v66 h-150 a10,10 0 0 1 -10,-10 v-46 a10,10 0 0 1 10,-10 z`, G.pink()));
  add(o, gloss(305, gy - 92, 140, 8));
  add(o, R(300, gy - 44, 150, 18, G.pinkDark(), { rx: 3 }));
  add(o, panelLines([[380, gy - 98, 380, gy - 44]]));
  // boom, cylinders, bucket
  add(o, Pa(`M330,${gy - 96} L140,${gy - 146} a10,10 0 0 0 -12,14 L322,${gy - 70} z`, G.pink()));
  add(o, L(320, gy - 84, 150, gy - 134, { c: 'rgba(255,255,255,.35)', w: 2 }));
  add(o, cylinder(384, gy - 104, 196, gy - 152, 7));
  add(o, cylinder(412, gy - 124, 244, gy - 170, 6));
  add(o, Pa(`M150,${gy - 152} H42 a12,12 0 0 0 -12,10 L22,${gy - 40} l38,36 h110 l6,-116 z`, G.pink()));
  add(o, Pa(`M150,${gy - 152} H42 l4,10 h100 z`, 'rgba(255,255,255,.3)', { s: 'none' }));
  add(
    o,
    Pg(`22,${gy - 40} 60,${gy - 4} 170,${gy - 4} 170,${gy - 20} 66,${gy - 20} 36,${gy - 46}`, G.pinkDark()),
  );
  for (let i = 0; i < 6; i++)
    add(
      o,
      Pg(
        `${26 + i * 22},${gy - 8} ${40 + i * 22},${gy - 8} ${37 + i * 22},${gy + 2} ${29 + i * 22},${gy + 2}`,
        '#3a353f',
        { s: '#1b191e' },
      ),
    );
  add(o, hose(470, gy - 96, 340, gy - 92, 14));
  add(o, hose(470, gy - 90, 300, gy - 80, 18, 2));
  // accessories
  add(o, capsule(864, gy - 130, 16, 44, '#d23b3b', { s: '#7a1f1f' }));
  add(o, R(866, gy - 124, 12, 6, 'rgba(255,255,255,.5)', { rx: 2, s: 'none' }));
  add(o, light(882, gy - 138));
  add(o, light(300, gy - 104, 4));
  add(o, light(858, gy - 56, 4));
  add(o, tyre(700, gy - 52, 58));
  add(o, tyre(372, gy - 52, 58));
  return o;
}

/** Demo line 1537. `planLHD` — the loader, plan view. */
function planLHD(model: ModelCode, p: Prims): SvgNode[] {
  const { G } = p;
  const bat = model === 'ML-21E';
  const cy = 120;
  const o: SvgNode[] = [];
  add(o, R(560, cy - 70, 300, 140, G.pink(), { rx: 10 }));
  if (bat) {
    add(o, R(600, cy - 52, 232, 104, G.white(), { rx: 6 }));
    for (let i = 0; i < 6; i++) add(o, R(606 + i * 38, cy - 46, 30, 92, '#d6d1da', { rx: 3, s: '#7d7784' }));
  } else {
    add(o, R(600, cy - 50, 150, 100, G.white(), { rx: 6 }));
    add(o, louvre(604, cy - 46, 142, 92, 9));
  }
  add(o, R(590, cy - 118, 100, 52, G.white(), { rx: 6 }));
  add(o, R(600, cy - 110, 80, 36, G.glass(), { rx: 3, s: '#5b6b74' }));
  add(o, R(438, cy - 30, 34, 60, G.dark(), { rx: 6 }));
  add(o, R(300, cy - 60, 150, 120, G.pink(), { rx: 8 }));
  add(o, R(140, cy - 22, 200, 44, G.pink(), { rx: 4 }));
  add(o, Pg(`170,${cy - 92} 22,${cy - 92} 22,${cy + 92} 170,${cy + 92}`, G.pinkDark()));
  add(o, R(30, cy - 80, 132, 164, 'rgba(255,255,255,.12)', { rx: 3, s: 'none' }));
  for (const x of [372, 700])
    for (const y of [cy - 94, cy + 46]) add(o, R(x - 32, y, 64, 48, G.tyre(), { rx: 6 }));
  return o;
}

// ---------------------------------------------------------------------------
// Body builders — TRUCK (demo lines 1538–1567)
// ---------------------------------------------------------------------------

/** Demo lines 1539–1564. `sideTruck` — the haul truck, side elevation. */
function sideTruck(model: ModelCode, p: Prims): SvgNode[] {
  const { G, shadow, tyre, cylinder } = p;
  const big = model === 'MT-63';
  const gy = 300;
  const o: SvgNode[] = [shadow(500, gy + 6, 440)];
  add(o, R(120, gy - 64, 760, 26, G.pinkDark(), { rx: 4 }));
  add(o, [bolt(160, gy - 51), bolt(420, gy - 51), bolt(700, gy - 51)]);
  add(o, Pa(`M90,${gy - 150} h160 a10,10 0 0 1 10,10 v86 h-180 v-86 a10,10 0 0 1 10,-10 z`, G.white()));
  add(o, R(104, gy - 142, 84, 54, G.glass(), { rx: 4, s: '#5b6b74' }));
  add(o, L(110, gy - 94, 150, gy - 138, { c: 'rgba(255,255,255,.75)', w: 3 }));
  add(o, R(84, gy - 156, 182, 9, G.dark(), { rx: 3 }));
  add(o, R(196, gy - 140, 58, 42, '#c5c0ca', { rx: 4 }));
  add(o, R(104, gy - 82, 84, 16, '#3a353f', { rx: 3 }));
  add(o, beacon(100, gy - 158));
  add(o, light(92, gy - 122));
  add(o, light(92, gy - 108, 4));
  add(o, Pa(`M262,${gy - 140} h118 v80 h-118 a8,8 0 0 1 -8,-8 v-64 a8,8 0 0 1 8,-8 z`, G.pink()));
  add(o, gloss(268, gy - 134, 104, 8));
  add(o, R(272, gy - 130, 98, 52, G.white(), { rx: 5 }));
  add(o, louvre(276, gy - 126, 90, 44, 6));
  add(o, R(330, gy - 178, 16, 40, G.dark(), { rx: 3 }));
  add(o, capsule(324, gy - 190, 28, 14, '#7d7784'));
  add(o, capsule(248, gy - 188, 14, 40, '#d23b3b', { s: '#7a1f1f' }));
  add(o, R(384, gy - 118, 30, 62, G.dark(), { rx: 6 }));
  add(o, C(399, gy - 87, 9, '#8d8794', { s: '#2a2530' }));
  add(
    o,
    Pa(
      `M420,${gy - 100} L880,${gy - 84} a10,10 0 0 0 12,-10 L890,${gy - 192} L480,${gy - 206} L430,${gy - 170} z`,
      G.pink(),
    ),
  );
  add(o, Pa(`M478,${gy - 208} L892,${gy - 194} l4,-10 L476,${gy - 218} z`, G.pinkDark()));
  add(o, L(440, gy - 172, 880, gy - 157, { c: 'rgba(0,0,0,.3)', w: 2 }));
  add(o, L(450, gy - 198, 870, gy - 184, { c: 'rgba(255,255,255,.45)', w: 3 }));
  add(
    o,
    panelLines([
      [600, gy - 200, 604, gy - 92],
      [740, gy - 196, 744, gy - 88],
    ]),
  );
  add(o, cylinder(432, gy - 96, 472, gy - 190, 8));
  add(o, R(760, gy - 64, 124, 14, G.steel(), { rx: 3 }));
  add(o, light(884, gy - 100, 4));
  add(o, light(884, gy - 112, 4));
  add(o, tyre(200, gy - 56, 60));
  add(o, tyre(big ? 700 : 680, gy - 56, 60));
  if (big) add(o, tyre(830, gy - 56, 60));
  return o;
}

/** Demo lines 1565–1567. `planTruck` — the haul truck, plan view. */
function planTruck(model: ModelCode, p: Prims): SvgNode[] {
  const { G } = p;
  const big = model === 'MT-63';
  const cy = 120;
  const o: SvgNode[] = [];
  add(o, R(90, cy - 62, 170, 124, G.white(), { rx: 10 }));
  add(o, R(104, cy - 50, 80, 100, G.glass(), { rx: 3, s: '#5b6b74' }));
  add(o, R(262, cy - 70, 118, 140, G.pink(), { rx: 8 }));
  add(o, louvre(272, cy - 60, 98, 120, 10));
  add(o, R(384, cy - 30, 30, 60, G.dark(), { rx: 6 }));
  add(o, Pg(`420,${cy - 88} 890,${cy - 96} 890,${cy + 96} 420,${cy + 88}`, G.pink()));
  add(o, Pg(`440,${cy - 70} 872,${cy - 78} 872,${cy + 78} 440,${cy + 70}`, '#f3b7d3', { s: '#b02a72' }));
  for (const x of [200, big ? 700 : 680, ...(big ? [830] : [])])
    for (const y of [cy - 106, cy + 54]) add(o, R(x - 34, y, 68, 52, G.tyre(), { rx: 6 }));
  return o;
}

// ---------------------------------------------------------------------------
// Body builders — JUMBO (demo lines 1569–1586)
// ---------------------------------------------------------------------------

/** Demo lines 1570–1582. `sideJumbo` — the twin-boom development jumbo, side elevation. */
function sideJumbo(_model: ModelCode, p: Prims): SvgNode[] {
  const { G, shadow, tyre, cylinder } = p;
  const gy = 300;
  const o: SvgNode[] = [shadow(420, gy + 6, 520)];
  add(o, Pa(`M560,${gy - 122} h310 a10,10 0 0 1 10,10 v58 h-320 z`, G.pink()));
  add(o, gloss(566, gy - 116, 290, 8));
  add(o, R(600, gy - 112, 120, 52, G.white(), { rx: 5 }));
  add(o, louvre(604, gy - 108, 112, 44, 6));
  add(o, R(740, gy - 112, 110, 52, '#3a353f', { rx: 4 }));
  add(o, louvre(744, gy - 108, 102, 44, 5));
  add(o, C(850, gy - 152, 46, '#f3b7d3', { s: '#b02a72', sw: 1.2 }));
  add(o, C(850, gy - 152, 36, G.white()));
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    add(o, L(850, gy - 152, 850 + Math.cos(a) * 36, gy - 152 + Math.sin(a) * 36, { c: '#8d8794', w: 1.2 }));
  }
  add(o, C(850, gy - 152, 9, G.dark()));
  add(o, hose(820, gy - 140, 700, gy - 70, 40, 3));
  add(o, R(740, gy - 164, 60, 40, G.pinkDark(), { rx: 4 }));
  add(o, louvre(744, gy - 160, 52, 32, 4));
  add(o, R(560, gy - 54, 320, 20, G.pinkDark(), { rx: 3 }));
  add(o, Pa(`M410,${gy - 210} h150 a10,10 0 0 1 10,10 v130 h-170 v-130 a10,10 0 0 1 10,-10 z`, G.white()));
  add(o, R(422, gy - 200, 128, 72, G.glass(), { rx: 5, s: '#5b6b74' }));
  add(o, L(430, gy - 136, 470, gy - 196, { c: 'rgba(255,255,255,.75)', w: 3 }));
  add(o, R(402, gy - 218, 166, 10, G.dark(), { rx: 3 }));
  add(o, R(424, gy - 120, 124, 26, '#3a353f', { rx: 3 }));
  add(o, beacon(560, gy - 220));
  add(o, light(414, gy - 120, 4));
  add(o, Pa(`M300,${gy - 100} h110 v50 h-110 a8,8 0 0 1 -8,-8 v-34 a8,8 0 0 1 8,-8 z`, G.pink()));
  add(o, R(300, gy - 54, 260, 20, G.pinkDark(), { rx: 3 }));
  for (const x of [330, 590, 860]) {
    add(o, cylinder(x, gy - 54, x, gy - 6, 6));
    add(o, R(x - 18, gy - 10, 36, 9, G.dark(), { rx: 2 }));
  }
  for (const [y0, dy] of [
    [gy - 152, -42],
    [gy - 120, -6],
  ]) {
    add(o, R(278, y0 - 16, 44, 32, G.dark(), { rx: 5 }));
    add(o, Pa(`M290,${y0 - 11} L62,${y0 + dy - 11} a10,10 0 0 0 0,22 L290,${y0 + 11} z`, G.pink()));
    add(o, L(280, y0 - 4, 72, y0 + dy - 4, { c: 'rgba(255,255,255,.35)', w: 2 }));
    add(o, cylinder(244, y0 + 12, 124, y0 + dy + 26, 5));
    add(o, R(-10, y0 + dy - 48, 330, 12, G.steel(), { rx: 2 }));
    add(o, R(-10, y0 + dy - 36, 330, 6, '#7d7784', { rx: 1, s: '#4a4350' }));
    for (let i = 0; i < 8; i++)
      add(o, R(10 + i * 40, y0 + dy - 46, 20, 8, 'rgba(255,255,255,.35)', { rx: 1, s: 'none' }));
    add(o, R(228, y0 + dy - 60, 64, 28, G.steel(), { rx: 4 }));
    add(o, R(236, y0 + dy - 66, 22, 8, G.dark(), { rx: 2 }));
    add(o, hose(300, y0, 240, y0 + dy - 48, 34));
    add(o, hose(310, y0 + 4, 250, y0 + dy - 46, 44, 2));
    add(o, L(-10, y0 + dy - 42, -62, y0 + dy - 42, { c: '#7d7784', w: 4 }));
    add(o, R(-70, y0 + dy - 48, 10, 12, G.dark(), { rx: 1 }));
  }
  add(o, capsule(552, gy - 172, 14, 40, '#d23b3b', { s: '#7a1f1f' }));
  add(o, light(300, gy - 104, 4));
  add(o, light(880, gy - 122, 4));
  add(o, tyre(360, gy - 52, 52));
  add(o, tyre(780, gy - 52, 52));
  return o;
}

/** Demo lines 1583–1586. `planJumbo` — the jumbo, plan view. */
function planJumbo(_model: ModelCode, p: Prims): SvgNode[] {
  const { G } = p;
  const cy = 120;
  const o: SvgNode[] = [];
  add(o, R(560, cy - 60, 320, 120, G.pink(), { rx: 8 }));
  add(o, R(600, cy - 50, 120, 100, G.white(), { rx: 5 }));
  add(o, louvre(604, cy - 46, 112, 92, 9));
  add(o, C(850, cy, 46, '#f3b7d3', { s: '#b02a72' }));
  add(o, R(410, cy - 56, 150, 112, G.white(), { rx: 10 }));
  add(o, R(422, cy - 46, 126, 92, G.glass(), { rx: 3, s: '#5b6b74' }));
  add(o, R(300, cy - 40, 110, 80, G.pink(), { rx: 6 }));
  for (const y of [cy - 52, cy + 52]) {
    add(o, R(278, y - 12, 44, 24, G.dark(), { rx: 5 }));
    add(o, Pg(`290,${y - 8} 60,${y - 8} 60,${y + 8} 290,${y + 8}`, G.pink()));
    add(o, R(-10, y - 6, 330, 12, G.steel(), { rx: 2 }));
    add(o, L(-10, y, -62, y, { c: '#7d7784', w: 4 }));
  }
  for (const x of [360, 780])
    for (const y of [cy - 94, cy + 46]) add(o, R(x - 30, y, 60, 46, G.tyre(), { rx: 6 }));
  return o;
}

// ---------------------------------------------------------------------------
// Body builders — PRODUCTION DRILL (demo lines 1588–1605)
// ---------------------------------------------------------------------------

/** Demo lines 1589–1602. `sideLonghole` — the longhole production drill, side elevation. */
function sideLonghole(model: ModelCode, p: Prims): SvgNode[] {
  const { G, shadow, tyre, cylinder } = p;
  const auto = model === 'MP-60A';
  const gy = 300;
  const o: SvgNode[] = [shadow(480, gy + 6, 380)];
  add(o, Pa(`M520,${gy - 118} h290 a10,10 0 0 1 10,10 v54 h-300 z`, G.pink()));
  add(o, gloss(526, gy - 112, 270, 8));
  add(o, R(560, gy - 108, 110, 50, G.white(), { rx: 5 }));
  add(o, louvre(564, gy - 104, 102, 42, 6));
  add(o, C(790, gy - 148, 42, '#f3b7d3', { s: '#b02a72', sw: 1.2 }));
  add(o, C(790, gy - 148, 33, G.white()));
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    add(o, L(790, gy - 148, 790 + Math.cos(a) * 33, gy - 148 + Math.sin(a) * 33, { c: '#8d8794', w: 1.2 }));
  }
  add(o, C(790, gy - 148, 8, G.dark()));
  add(o, R(690, gy - 158, 60, 40, G.pinkDark(), { rx: 4 }));
  add(o, louvre(694, gy - 154, 52, 32, 4));
  add(o, R(520, gy - 54, 300, 20, G.pinkDark(), { rx: 3 }));
  if (auto) {
    add(o, Pa(`M390,${gy - 172} h130 a8,8 0 0 1 8,8 v94 h-138 z`, G.white()));
    add(o, R(400, gy - 162, 110, 42, '#c5c0ca', { rx: 4 }));
    add(o, R(404, gy - 114, 102, 26, '#3a353f', { rx: 3 }));
    add(o, L(456, gy - 172, 456, gy - 228, { w: 2.4 }));
    add(o, R(440, gy - 250, 32, 24, G.dark(), { rx: 5 }));
    add(o, C(456, gy - 238, 6, '#39b06a', { s: 'none' }));
    add(o, C(430, gy - 140, 7, '#2a2530'));
    add(o, C(430, gy - 140, 3, '#3aa0ff', { s: 'none' }));
    add(o, C(486, gy - 140, 7, '#2a2530'));
    add(o, C(486, gy - 140, 3, '#3aa0ff', { s: 'none' }));
    add(
      o,
      S(
        'text',
        {
          x: 456,
          y: gy - 98,
          text_anchor: 'middle',
          font_size: 8,
          fill: '#3a353f',
          font_family: 'IBM Plex Mono,monospace',
          font_weight: 600,
        },
        'AUTONOMOUS',
      ),
    );
  } else {
    add(o, Pa(`M390,${gy - 202} h130 a8,8 0 0 1 8,8 v124 h-138 v-124 a8,8 0 0 1 8,-8 z`, G.white()));
    add(o, R(402, gy - 192, 108, 66, G.glass(), { rx: 5, s: '#5b6b74' }));
    add(o, L(410, gy - 134, 446, gy - 188, { c: 'rgba(255,255,255,.75)', w: 3 }));
    add(o, R(382, gy - 210, 144, 10, G.dark(), { rx: 3 }));
    add(o, R(404, gy - 118, 104, 24, '#3a353f', { rx: 3 }));
    add(o, beacon(520, gy - 212));
  }
  add(o, Pa(`M300,${gy - 98} h90 v48 h-90 a8,8 0 0 1 -8,-8 v-32 a8,8 0 0 1 8,-8 z`, G.pink()));
  add(o, R(300, gy - 54, 220, 20, G.pinkDark(), { rx: 3 }));
  for (const x of [326, 560, 800]) {
    add(o, cylinder(x, gy - 54, x, gy - 6, 6));
    add(o, R(x - 18, gy - 10, 36, 9, G.dark(), { rx: 2 }));
  }
  add(o, Pa(`M300,${gy - 94} L210,${gy - 122} a10,10 0 0 0 0,22 L300,${gy - 70} z`, G.pink()));
  add(o, cylinder(282, gy - 70, 226, gy - 98, 5));
  add(o, R(158, gy - 264, 64, 26, G.dark(), { rx: 5 }));
  add(o, R(176, gy - 282, 24, 284, G.steel(), { rx: 3 }));
  add(o, R(184, gy - 282, 8, 284, '#7d7784', { rx: 1, s: '#4a4350' }));
  for (let i = 0; i < 12; i++)
    add(o, R(178, gy - 274 + i * 24, 20, 6, 'rgba(255,255,255,.35)', { rx: 1, s: 'none' }));
  add(o, R(158, gy - 298, 60, 32, G.steel(), { rx: 5 }));
  add(o, R(166, gy - 308, 24, 10, G.dark(), { rx: 2 }));
  add(o, hose(300, gy - 90, 200, gy - 300, 60, 3));
  add(o, L(188, gy - 262, 188, gy + 2, { c: '#7d7784', w: 4 }));
  add(o, R(220, gy - 242, 42, 112, G.steel(), { rx: 8 }));
  for (let i = 0; i < 5; i++) {
    add(o, C(241, gy - 230 + i * 22, 6, '#2a2530'));
    add(o, C(241, gy - 230 + i * 22, 3, '#8d8794', { s: 'none' }));
  }
  add(o, capsule(510, gy - 168, 14, 40, '#d23b3b', { s: '#7a1f1f' }));
  add(o, light(300, gy - 102, 4));
  add(o, light(820, gy - 118, 4));
  add(o, tyre(350, gy - 52, 50));
  add(o, tyre(730, gy - 52, 50));
  return o;
}

/** Demo lines 1603–1605. `planLonghole` — the longhole drill, plan view. */
function planLonghole(_model: ModelCode, p: Prims): SvgNode[] {
  const { G } = p;
  const cy = 120;
  const o: SvgNode[] = [];
  add(o, R(520, cy - 58, 300, 116, G.pink(), { rx: 8 }));
  add(o, R(560, cy - 50, 110, 100, G.white(), { rx: 5 }));
  add(o, C(790, cy, 42, '#f3b7d3', { s: '#b02a72' }));
  add(o, R(390, cy - 56, 130, 112, G.white(), { rx: 10 }));
  add(o, R(402, cy - 46, 106, 92, G.glass(), { rx: 3, s: '#5b6b74' }));
  add(o, R(300, cy - 40, 90, 80, G.pink(), { rx: 6 }));
  add(o, Pg(`300,${cy - 10} 210,${cy - 10} 210,${cy + 10} 300,${cy + 10}`, G.pink()));
  add(o, R(158, cy - 20, 64, 40, G.steel(), { rx: 5 }));
  add(o, R(220, cy + 30, 42, 40, G.steel(), { rx: 8 }));
  for (const x of [350, 730])
    for (const y of [cy - 92, cy + 44]) add(o, R(x - 28, y, 56, 46, G.tyre(), { rx: 6 }));
  return o;
}

// ---------------------------------------------------------------------------
// Anchors and builder tables — demo lines 1607–1613
// ---------------------------------------------------------------------------

/**
 * Demo lines 1607–1612. `ANCH` — component name → `[x, y]` in the side-view
 * user space, per machine class. Transcribed verbatim.
 *
 * Only the four classes with a body builder appear; `sdrill` has none.
 */
export const ILLUSTRATION_ANCHORS: Readonly<
  Record<string, Readonly<Record<string, readonly [number, number]>>>
> = {
  lhd: {
    Engine: [675, 196],
    'Traction motor & inverter': [770, 238],
    'Battery pack (LFP, 300 kWh)': [716, 222],
    'Battery thermal management': [820, 214],
    'Charging interface': [846, 214],
    Transmission: [600, 266],
    'Torque converter': [560, 250],
    'Hydraulic pump & drive': [516, 236],
    'Rear axle & brakes': [700, 262],
    'Front axle & brakes': [372, 262],
    'Boom & bucket cylinders': [236, 178],
    'Bucket & GET': [92, 232],
    Tyres: [372, 302],
    Cab: [640, 150],
    'Remote control module & cameras': [636, 120],
    'Fire suppression': [872, 190],
  },
  truck: {
    Engine: [320, 206],
    Transmission: [330, 262],
    'Retarder & service brakes': [700, 262],
    'Front axle': [200, 262],
    'Rear axles': [760, 262],
    Suspension: [640, 262],
    'Dump body & hoist cylinders': [660, 150],
    Tyres: [830, 302],
    Cab: [150, 200],
    'Fire suppression': [255, 130],
  },
  jumbo: {
    'LH boom': [180, 140],
    'RH boom': [180, 180],
    'LH rock drill': [262, 100],
    'RH rock drill': [262, 170],
    'LH feed': [80, 110],
    'RH feed': [80, 175],
    'Hydraulic power pack': [770, 158],
    'Electric motor & cable reel': [850, 150],
    'Carrier engine (diesel)': [660, 215],
    'Cab & canopy': [485, 150],
    'Water & flushing system': [340, 240],
    'Tyres & axles': [780, 262],
  },
  longhole: {
    'Boom & feed': [188, 120],
    'Rock drill': [188, 10],
    'Rod carousel': [240, 120],
    'Hydraulic power pack': [720, 165],
    'Electric motor & cable reel': [790, 152],
    'Carrier engine (diesel)': [615, 220],
    'Cab & canopy': [455, 165],
    'Autonomy sensors & controller': [456, 64],
    'Tyres & axles': [730, 262],
  },
};

/** Demo line 1613. `SIDES` — class → side-elevation builder. */
const SIDES: Partial<Record<MachineClass, Builder>> = {
  lhd: sideLHD,
  truck: sideTruck,
  jumbo: sideJumbo,
  longhole: sideLonghole,
};

/** Demo line 1613. `PLANS` — class → plan-view builder. */
const PLANS: Partial<Record<MachineClass, Builder>> = {
  lhd: planLHD,
  truck: planTruck,
  jumbo: planJumbo,
  longhole: planLonghole,
};

/**
 * True when this class has a drawing. The demo has no builder for `sdrill`
 * (demo line 1613), so it has no illustration and none is invented here.
 */
export function hasIllustration(cls: MachineClass): boolean {
  return SIDES[cls] !== undefined;
}

// ---------------------------------------------------------------------------
// illustration() — demo lines 1615–1622
// ---------------------------------------------------------------------------

/** Demo line 1615. `vb` — the fixed viewBox per view. Never computed from data. */
const VIEWBOX: Readonly<Record<IllustrationView, string>> = {
  plan: '-80 0 1040 240',
  side: '-80 -40 1040 370',
};

/** Demo line 1617. The three bands that earn a pulsing callout. */
const CALLOUT_BANDS = ['major', 'vpoor', 'poor'] as const;

/**
 * Demo line 1618. The callout colour per band.
 *
 * **Deviation:** the demo hard-codes `{major:'#2B1B2E', vpoor:'#D32F2F',
 * poor:'#E67E22'}`; this port uses the equivalent theme tokens from `oem.css`
 * so the callouts track light/dark.
 */
const CALLOUT_COLOR: Readonly<Record<(typeof CALLOUT_BANDS)[number], string>> = {
  major: 'var(--band-major)',
  vpoor: 'var(--band-vpoor)',
  poor: 'var(--band-poor)',
};

/** Demo line 1618. The callout label per band. */
const CALLOUT_LABEL: Readonly<Record<(typeof CALLOUT_BANDS)[number], string>> = {
  major: 'Major issue',
  vpoor: 'Very poor',
  poor: 'Poor',
};

/**
 * The gradient-id namespace for one render.
 *
 * **Deviation from demo line 1615**, which uses an incrementing module counter
 * (`'il' + (++ILN)`). Deriving the namespace from `machineId` + `view` keeps
 * the output identical across renders, which the parity snapshots need.
 * Everything outside `[A-Za-z0-9-]` becomes `-`, so the result is always a
 * legal id fragment.
 */
const illustrationUid = (machineId: string, view: IllustrationView): string =>
  `il-${machineId}-${view}`.replace(/[^A-Za-z0-9-]/g, '-');

/** One gradient stop. Demo line 1616. */
const stop = (offset: string, color: string, opacity?: string): SvgNode => ({
  tag: 'stop',
  attrs:
    opacity === undefined
      ? { offset, 'stop-color': color }
      : { offset, 'stop-color': color, 'stop-opacity': opacity },
});

/**
 * Demo line 1616. `defs` — the nine gradients and the drop-shadow filter, with
 * every stop offset and colour transcribed verbatim, as a node tree.
 */
const defsNodes = (uid: string, livery: LiveryPalette): SvgNode[] => [
  {
    tag: 'linearGradient',
    attrs: { id: `gp${uid}`, x1: '0', y1: '0', x2: '0', y2: '1' },
    children: [
      stop('0', livery.paint[0]),
      stop('.45', livery.paint[1]),
      stop('1', livery.paint[2]),
    ],
  },
  {
    tag: 'linearGradient',
    attrs: { id: `gpd${uid}`, x1: '0', y1: '0', x2: '0', y2: '1' },
    children: [stop('0', livery.paintDark[0]), stop('1', livery.paintDark[1])],
  },
  {
    tag: 'linearGradient',
    attrs: { id: `gw${uid}`, x1: '0', y1: '0', x2: '0', y2: '1' },
    children: [stop('0', '#FFFFFF'), stop('1', '#D9D3DA')],
  },
  {
    tag: 'linearGradient',
    attrs: { id: `gs${uid}`, x1: '0', y1: '0', x2: '0', y2: '1' },
    children: [stop('0', '#D9D5DD'), stop('.5', '#A39DA8'), stop('1', '#6F6975')],
  },
  {
    tag: 'linearGradient',
    attrs: { id: `gk${uid}`, x1: '0', y1: '0', x2: '0', y2: '1' },
    children: [stop('0', '#4A4350'), stop('1', '#1F1B23')],
  },
  {
    tag: 'linearGradient',
    attrs: { id: `gg${uid}`, x1: '0', y1: '0', x2: '1', y2: '1' },
    children: [stop('0', '#E4F1F7'), stop('.6', '#9FC3D3'), stop('1', '#5E8A9C')],
  },
  {
    tag: 'radialGradient',
    attrs: { id: `gt${uid}`, cx: '.4', cy: '.35', r: '.8' },
    children: [stop('0', '#3A363F'), stop('1', '#141216')],
  },
  {
    tag: 'radialGradient',
    attrs: { id: `gr${uid}`, cx: '.35', cy: '.3', r: '.9' },
    children: [stop('0', '#C9C5CE'), stop('1', '#6F6975')],
  },
  {
    tag: 'radialGradient',
    attrs: { id: `gsh${uid}` },
    children: [stop('0', '#2b2730', '.28'), stop('1', '#2b2730', '0')],
  },
  {
    tag: 'filter',
    attrs: { id: `sh${uid}`, x: '-5%', y: '-10%', width: '110%', height: '130%' },
    children: [
      {
        tag: 'feDropShadow',
        attrs: {
          dx: '0',
          dy: '5',
          stdDeviation: '5',
          'flood-color': '#2b2730',
          'flood-opacity': '.22',
        },
      },
    ],
  },
];

/**
 * Demo lines 1615–1622. `illustration(f, health, sel, view)` — the machine
 * drawing with its condition callouts and component anchor pins.
 *
 * The body and the `<defs>` are the ported node trees, rendered as JSX by
 * {@link SvgNodes}. The interactive layer — flagged hotspots, callout labels,
 * anchor pins — is written as JSX directly, so component names are escaped by
 * React and every click is a React handler. No markup string and no `onclick=`
 * attribute reaches the DOM.
 *
 * Returns `null` for a class with no builder (`sdrill`).
 */
export function MachineIllustration({
  machineId,
  model,
  cls,
  view,
  health,
  selected = null,
  onSelect,
}: MachineIllustrationProps): JSX.Element | null {
  const { brandId } = useBrand();
  const livery = LIVERY[brandId];
  const build = (view === 'plan' ? PLANS : SIDES)[cls];
  if (!build) return null;

  const uid = illustrationUid(machineId, view);
  const draw = build(model, makePrims(uid));
  const vb = VIEWBOX[view];
  const anch = ILLUSTRATION_ANCHORS[cls] ?? {};

  /**
   * The shared activation props for one clickable element. Applied only when an
   * `onSelect` is supplied, so a read-only illustration exposes no empty
   * buttons to the keyboard.
   */
  const interactive = (component: string) =>
    onSelect
      ? {
          role: 'button',
          tabIndex: 0,
          style: { cursor: 'pointer' as const },
          onClick: () => onSelect(component),
          onKeyDown: (e: KeyboardEvent<SVGGElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(component);
            }
          },
        }
      : {};

  /**
   * Demo line 1617. `flagged` — rows in a callout band that also have an anchor
   * for this class. Side view only; the plan view draws no hotspots at all.
   *
   * The demo reads the mutated `h.band`; this port derives it with
   * `healthBand()`.
   */
  const flagged =
    view === 'side'
      ? health.filter((h) => {
          const band = healthBand(h);
          return (CALLOUT_BANDS as readonly string[]).includes(band) && Boolean(anch[h.component]);
        })
      : [];

  /** Demo line 1618. One flagged row's resolved callout geometry. */
  const callouts = flagged.map((h, i) => {
    const band = healthBand(h) as (typeof CALLOUT_BANDS)[number];
    const [x, y] = anch[h.component];
    const col = CALLOUT_COLOR[band] || K.warn;
    const lab = `${h.component} · ${CALLOUT_LABEL[band]} · ${h.health_score}/100`;
    const lw = lab.length * 6.3 + 16;
    // Demo line 1618: the default position. The demo then lets the pointer move
    // it and remembers the result in LABELPOS; this port has no drag, so the
    // default is the only position.
    const lx = Math.max(-70, Math.min(x - lw / 2 + (i % 2 ? 60 : -60), 940 - lw));
    const ly = -34 + (i % 2) * 26;
    return { h, x, y, col, lab, lw, lx, ly };
  });

  return (
    <svg viewBox={vb} className="illus" role="img" aria-label={`${model} ${view} view`} data-uid={uid}>
      {/* Demo line 1616. Static gradient + filter defs, namespaced by uid. */}
      <defs>
        <SvgNodes nodes={defsNodes(uid, livery)} />
      </defs>

      {/* Demo line 1622. The machine body: the ported geometry node tree. */}
      <g filter={`url(#sh${uid})`}>
        <SvgNodes nodes={draw} />
      </g>

      {/* Demo line 1619. The pulsing hotspot + leader line for each flagged component. */}
      {callouts.map(({ h, x, y, col, lw, lx, ly }) => (
        <g
          key={`hs-${h.component}`}
          className={`hs ${healthAttention(h)}`}
          data-c={h.component}
          {...interactive(h.component)}
        >
          <title>{h.component}</title>
          <circle cx={x} cy={y} r={16} fill={col} fillOpacity=".18">
            <animate attributeName="r" values="12;22;12" dur="1.4s" repeatCount="indefinite" />
          </circle>
          <circle cx={x} cy={y} r={6.5} fill={col} stroke="#fff" strokeWidth={2} />
          <line
            className="ld"
            x1={x}
            y1={y}
            x2={lx + lw / 2}
            y2={ly + 20}
            stroke={col}
            strokeWidth={1.4}
            strokeDasharray="4 3"
          />
        </g>
      ))}

      {/* Demo line 1621. A plain pin on every anchor the machine has a register row for. */}
      {Object.entries(anch).map(([c, [x, y]]) => {
        if (view !== 'side') return null;
        if (!health.find((h) => h.component === c)) return null;
        const on = selected === c;
        return (
          <g key={`hp-${c}`} className={`hp ${on ? 'on' : ''}`} data-c={c} {...interactive(c)}>
            <title>{c}</title>
            <circle
              cx={x}
              cy={y}
              r={on ? 9 : 5.5}
              fill={on ? livery.pin : 'rgba(255,255,255,.9)'}
              stroke={livery.pin}
              strokeWidth={on ? 2.5 : 1.4}
            />
          </g>
        );
      })}

      {/* Demo line 1620. The callout labels, at their default positions (no drag). */}
      {callouts.map(({ h, col, lab, lw, lx, ly }) => (
        <g
          key={`hl-${h.component}`}
          className="hl"
          data-c={h.component}
          data-w={lw}
          transform={`translate(${lx},${ly})`}
          {...interactive(h.component)}
        >
          <title>{lab}</title>
          <rect x={0} y={0} width={lw} height={20} rx={5} fill={col} stroke="#fff" strokeWidth={1} />
          <text
            x={8}
            y={14}
            fontSize={10.5}
            fill="#fff"
            fontFamily="IBM Plex Sans,sans-serif"
            fontWeight={600}
          >
            {lab}
          </text>
        </g>
      ))}
    </svg>
  );
}
